import { Box, Card, Heading, Icon, Paragraph, Popover, Spinner, Tag, Text } from '@zillow/constellation-10';
import { IconArrowDownFilled, IconArrowUpFilled, IconInfoFilled } from '@zillow/constellation-icons';
import { formatNumber } from '../../../../../utils/formatNumber';

type SummaryMetricCardProps = {
  label: string;
  value: number | null;
  delta: number;
  valueFormatter?: (value: number) => string;
  deltaFormatter?: (value: number) => string;
  infoText?: string;
  isLoading?: boolean;
};

export const SummaryMetricCard = ({
  label,
  value,
  delta,
  valueFormatter = formatNumber,
  deltaFormatter = formatNumber,
  infoText,
  isLoading = false,
}: SummaryMetricCardProps) => {
  const hasData = value !== null;
  const formattedValue = hasData ? valueFormatter(value) : '—';
  const formattedDelta = hasData ? deltaFormatter(Math.abs(delta)) : '';
  const sign = delta > 0 ? '+' : delta < 0 ? '-' : '';
  const tone = delta > 0 ? 'green' : delta < 0 ? 'red' : 'gray';

  return (
    <Card elevated={false} css={{ padding: '600', display: 'flex', flexDirection: 'column', gap: '200' }}>
      <Text textStyle="body-sm-bold" css={{ display: 'flex', alignItems: 'center', gap: '100' }}>
        {label}
        {infoText && (
          <Popover
            trigger={
              <Icon css={{ cursor: 'pointer', fill: 'icon.muted' }} size="sm">
                <IconInfoFilled />
              </Icon>
            }
            header={<Text textStyle="body-sm-bold">{label}</Text>}
            closeButton={<span hidden />}
            body={<Paragraph>{infoText}</Paragraph>}
          />
        )}
      </Text>
      {isLoading ? (
        <Box css={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '48px' }}>
          <Spinner size="md" />
        </Box>
      ) : (
        <Box css={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '200' }}>
          <Heading level={4}>{formattedValue}</Heading>
          {hasData && delta !== 0 && !isNaN(delta) && (
            <Tag
              tone={tone}
              size="md"
              css={{ alignSelf: 'flex-start' }}
              icon={<Icon>{delta > 0 ? <IconArrowUpFilled /> : <IconArrowDownFilled />}</Icon>}
            >
              <Box css={{ display: 'flex', alignItems: 'center', gap: '100' }}>{`${sign}${formattedDelta}`} MoM</Box>
            </Tag>
          )}
        </Box>
      )}
    </Card>
  );
};
